import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '@/lib/api';

const AuthContext = createContext(null);

// Token storage utilities - Note: For enhanced security in production,
// consider using httpOnly cookies with SameSite attribute
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'user';

const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
const getStoredUser = () => {
  const saved = localStorage.getItem(USER_KEY);
  return saved ? JSON.parse(saved) : null;
};
const setStoredAuth = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};
const clearStoredAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verify session on mount
  useEffect(() => {
    const token = getStoredToken();
    const savedUser = getStoredUser();
    
    if (token && savedUser) {
      setUser(savedUser);
      // Verify token is still valid
      authApi.me()
        .then(res => {
          setUser(res.data);
          setStoredAuth(token, res.data);
        })
        .catch(() => {
          clearStoredAuth();
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await authApi.login({ email, password });
    const userData = response.data;
    setStoredAuth(userData.token, userData);
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const response = await authApi.register({ email, password, name });
    const userData = response.data;
    setStoredAuth(userData.token, userData);
    setUser(userData);
    return userData;
  }, []);

  const registerWithInvite = useCallback(async (inviteCode, password) => {
    const response = await authApi.registerWithInvite({ invite_code: inviteCode, password });
    const userData = response.data;
    setStoredAuth(userData.token, userData);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (e) {
      // Ignore errors on logout
    }
    clearStoredAuth();
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    login,
    register,
    registerWithInvite,
    logout,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager' || user?.role === 'admin',
    isInstaller: user?.role === 'installer',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
